import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  RefreshCw, 
  Truck,
  AlertTriangle,
  CheckCircle,
  Ban,
  ChevronRight,
  Filter
} from 'lucide-react';
import { getVehicles, createVehicle, fetchChallansForVehicle } from '../services/api';
import { Vehicle } from '../types';
import clsx from 'clsx';

export default function Vehicles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshingVehicle, setRefreshingVehicle] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getVehicles({ 
        status: statusFilter || undefined,
        search: search || undefined,
        limit: 50
      });
      if (response.success && response.data) {
        setVehicles(response.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  const handleRefreshVehicle = async (vehicleNumber: string) => {
    try {
      setRefreshingVehicle(vehicleNumber);
      await fetchChallansForVehicle(vehicleNumber);
      await fetchData();
    } catch (err: any) {
      alert('Failed to refresh: ' + err.message);
    } finally {
      setRefreshingVehicle(null);
    }
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    if (status) {
      setSearchParams({ status });
    } else {
      setSearchParams({});
    }
  };

  const statusBadge = (status: string) => {
    const styles = {
      clean: 'badge-success',
      pending: 'badge-warning',
      blocked: 'badge-danger',
      unknown: 'badge-info',
    };
    const icons = {
      clean: <CheckCircle className="w-3 h-3 mr-1" />,
      pending: <AlertTriangle className="w-3 h-3 mr-1" />,
      blocked: <Ban className="w-3 h-3 mr-1" />,
      unknown: <Truck className="w-3 h-3 mr-1" />,
    };
    return (
      <span className={clsx('flex items-center', styles[status as keyof typeof styles] || 'badge-info')}>
        {icons[status as keyof typeof icons]}
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Vehicles</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your fleet vehicles and monitor challans</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by vehicle number or transporter..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>
          </form>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="">All Status</option>
              <option value="clean">Clean</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicle List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="card p-6 text-center">
          <p className="text-danger-600 mb-4">{error}</p>
          <button onClick={fetchData} className="btn-primary">Retry</button>
        </div>
      ) : vehicles.length === 0 ? (
        <div className="card p-12 text-center">
          <Truck className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
          <p className="text-gray-500 mb-4">Add your first vehicle to start tracking challans</p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map((vehicle) => (
            <div key={vehicle.vehicleNumber} className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/vehicles/${vehicle.vehicleNumber}`)}>
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-gray-500" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <Link 
                      to={`/vehicles/${vehicle.vehicleNumber}`}
                      className="font-semibold text-gray-900 hover:text-primary-600"
                    >
                      {vehicle.vehicleNumber}
                    </Link>
                    {statusBadge(vehicle.compliance.status)}
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    {vehicle.transporterName || 'No transporter assigned'}
                  </p>
                </div>

                <div className="hidden sm:flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-gray-500">Pending</p>
                    <p className="font-semibold text-warning-600">
                      {vehicle.challanSummary.pendingChallans}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Amount</p>
                    <p className="font-semibold text-gray-900">
                      ₹{vehicle.challanSummary.totalAmountPending.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRefreshVehicle(vehicle.vehicleNumber); }}
                    disabled={refreshingVehicle === vehicle.vehicleNumber}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Refresh challans"
                  >
                    <RefreshCw className={clsx(
                      "w-4 h-4 text-gray-500",
                      refreshingVehicle === vehicle.vehicleNumber && "animate-spin"
                    )} />
                  </button>
                  <Link
                    to={`/vehicles/${vehicle.vehicleNumber}`}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <AddVehicleModal 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

function AddVehicleModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleNumber.trim()) {
      setError('Vehicle number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await createVehicle({ vehicleNumber: vehicleNumber.trim(), transporterName: transporterName.trim() || undefined });
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Vehicle</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-danger-50 text-danger-600 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Number *
            </label>
            <input
              type="text"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
              placeholder="MH12AB1234"
              className="input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transporter Name
            </label>
            <input
              type="text"
              value={transporterName}
              onChange={(e) => setTransporterName(e.target.value)}
              placeholder="ABC Logistics"
              className="input"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
